<template>
	<div class="date-picker-calendar">
		<div class="date-picker-calendar__header">
			<ButtonIcon
				class="date-picker-calendar__previous-btn"
				v-if="showPreviousMonthButton"
				iconClassName="icon-arrow_left"
				@click="onClickPreviousMonth"
			/>
			<ButtonSmall
				variant="text"
				:labelText="monthLabel"
				@click="onMonthLabelClick"
			/>
			<ButtonIcon
				class="date-picker-calendar__next-btn"
				v-if="showNextMonthButton"
				iconClassName="icon-arrow_right"
				@click="onClickNextMonth"
			/>
		</div>
		<table class="date-picker-calendar__table">
			<tr class="date-picker-calendar__weekdays">
				<th
					class="date-picker-calendar__weekdays-day"
					v-for="day in narrowDayValues"
					:key="day.id"
				>
					{{ day.dayCode }}
				</th>
			</tr>
			<tr
				class="date-picker-calendar__weeks-of-month"
				v-for="week in weeksOfMonth"
				:key="week.id"
			>
				<td
					:class="getClassList(day)"
					v-for="day in week"
					:key="day.toDateString()"
					@click="onDayClicked(day)"
					tabindex="0"
				>
					<span class="date-picker-calendar__weeks-of-month-day-text">
						{{ day.getDate() }}
					</span>
				</td>
			</tr>
		</table>
	</div>
</template>

<script>
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import ButtonSmall from '@/components/forms/button/button-small.vue';
import dateLocaleMixin from '@/mixins/date-locale-mixin';
import {
	addDays,
	compareAsc,
	compareDesc,
	getDay,
	getDaysInMonth,
	getMonth,
	getWeekOfMonth,
	isSameDay,
	lastDayOfWeek
} from 'date-fns';
import { isDate, startOfToday } from 'date-fns';

export default {
	name: 'DatePickerCalendar',
	mixins: [dateLocaleMixin],
	data() {
		return {
			monthNumber: undefined,
			year: undefined,
			narrowDayValues: this.getDayObjectArray(),
			weeksOfMonth: undefined,
			numberOfDaysInMonth: undefined,
			monthLabel: undefined,
			notSameMonthClass: 'not-same-month',
			isSelectedClass: 'is-selected',
			isRangeDayClass: 'is-range-day',
			isTodayClass: 'is-today',
			today: undefined
		};
	},
	components: {
		ButtonIcon,
		ButtonSmall
	},
	props: {
		/**
		 * Single Date object or range object in format:
		 * {
		 * 	startDate: Date,
		 * 	endDate: Date,
		 * }
		 */
		value: {
			required: true
		},
		selectionType: {
			type: String,
			required: true
		},
		monthDate: {
			type: Date,
			required: true
		},
		showPreviousMonthButton: {
			type: Boolean,
			default: false
		},
		showNextMonthButton: {
			type: Boolean,
			default: false
		},
		/**
		 * values narrow, abbreviated, wide
		 */
		dayNameWidth: {
			type: String,
			default: 'narrow'
		}
	},
	created() {
		this.monthNumber = this.monthDate.getMonth();
		this.year = this.monthDate.getFullYear();
		this.numberOfDaysInMonth = getDaysInMonth(this.monthDate);
		this.weeksOfMonth = this.getWeeksOfMonth();
		this.monthLabel = this.getMonthLabel();
		this.today = startOfToday();
	},
	computed: {},
	methods: {
		/**
		 * This function will return any programatic CSS class names for a day cell
		 * when:
		 * 		the date is not in the current month ('not-same-month')
		 * 		the date is selected ('is-selected')
		 * 		the date is between two selected dates ('is-range-day')
		 * 		the date is highlighted as today ('is-today')
		 */
		getClassList(day) {
			const classList = ['date-picker-calendar__weeks-of-month-day'];
			let dayNotInThisMonth;

			if (getMonth(day) !== this.monthNumber) {
				classList.push(this.notSameMonthClass);
				dayNotInThisMonth = true;
			}

			if (
				!dayNotInThisMonth &&
				this.value &&
				(isSameDay(this.value, day) ||
					isSameDay(this.value.startDate, day) ||
					isSameDay(this.value.endDate, day))
			) {
				classList.push(this.isSelectedClass);
			}

			if (
				!dayNotInThisMonth &&
				this.value &&
				this.value.startDate &&
				isDate(this.value.startDate) &&
				compareAsc(this.value.startDate, day) === -1 &&
				this.value.endDate &&
				isDate(this.value.endDate) &&
				compareDesc(this.value.endDate, day) === -1
			) {
				classList.push(this.isRangeDayClass);
			}

			if (
				isSameDay(day, this.today) &&
				getMonth(day) === this.monthNumber
			) {
				classList.push(this.isTodayClass);
			}

			return classList;
		},
		getWeeksOfMonth() {
			const weekArray = [];
			let dayArray = [];

			//go through all days in the month
			for (let index = 1; index <= this.numberOfDaysInMonth; index++) {
				const targetDate = new Date(this.year, this.monthNumber, index);
				const nextDate = addDays(targetDate, 1);
				const targetDayOfWeek = getDay(targetDate);

				//first day of the month may not be first day of week
				if (index === 1 && targetDayOfWeek > 0) {
					for (let index3 = 0; index3 < targetDayOfWeek; index3++) {
						const subtractindex = index3 - targetDayOfWeek;
						const previousMonthDay = addDays(
							targetDate,
							subtractindex
						);
						dayArray.push(previousMonthDay);
					}
				}

				dayArray.push(targetDate);

				//if it's the last day of the month,
				//see if we need to fill in the last week with days from next month
				if (index === this.numberOfDaysInMonth) {
					const lastDayDate = lastDayOfWeek(targetDate);
					const lastDay = getDay(lastDayDate);
					const targetDay = getDay(targetDate);

					if (lastDay > targetDay) {
						let nextMonthDayIndex = 1;

						for (
							let index2 = targetDay + 1;
							index2 <= lastDay;
							index2++
						) {
							const dayOfNextMonth = addDays(
								targetDate,
								nextMonthDayIndex
							);

							dayArray.push(dayOfNextMonth);
							nextMonthDayIndex += 1;
						}
					}
				}

				if (getWeekOfMonth(nextDate) !== getWeekOfMonth(targetDate)) {
					//weekArray is an array of array of days
					weekArray.push(dayArray);
					dayArray = [];
				}
			}

			return weekArray;
		},
		getDayObjectArray() {
			const dayValues = this.getDayValues(this.dayNameWidth); //function from locale mixin
			const dayObjectArray = [];

			for (let index = 0; index < dayValues.length; index++) {
				dayObjectArray.push({
					id: index,
					dayCode: dayValues[index]
				});
			}

			return dayObjectArray;
		},
		getMonthLabel() {
			return `${this.getMonthName()} ${this.year}`;
		},
		getMonthName() {
			return this.locale.localize.month(this.monthNumber, {
				width: 'wide'
			});
		},
		onClickPreviousMonth() {
			this.$emit('clickPreviousMonth');
		},
		onClickNextMonth() {
			this.$emit('clickNextMonth');
		},
		onDayClicked(day) {
			this.$emit('dayClicked', day);
		},
		onMonthLabelClick() {
			this.$emit('monthLabelClick');
		}
	}
};
</script>

<style lang="scss" scoped>
.date-picker-calendar {
	&__header {
		position: relative;
		text-align: center;
		margin-bottom: $gutter-lg;
		font-size: $heading-font-size-md;

		.btn-small.btn-small--text {
			font-size: $heading-font-size-md;
		}
	}

	@include date-picker-previous-next;

	&__table {
		border-spacing: 0;
	}

	&__weekdays-day {
		padding: $padding-sm;
	}

	&__weekdays-day,
	&__weeks-of-month-day {
		width: 32px;
		height: 32px;
	}

	&__weeks-of-month-day {
		text-align: center;
		cursor: pointer;
		user-select: none;
		border: $focus-width $focus-style var(--neutral-2);
		border-color: transparent;
		border-radius: $border-radius;

		&:hover,
		&.is-selected {
			background: var(--bg-brand-2);
			color: var(--bg-brand-2-fg-2);
		}

		&:focus {
			border-color: var(--bg-brand-2);
		}

		&.is-today {
			position: relative;
			border-radius: 50%;
			border-color: var(--neutral-5);

			&.is-selected {
				border-radius: $border-radius;
				border-color: var(--bg-brand-2);

				&::after {
					content: '';
					@include transform-full-center;
					width: calc(100% + 4px);
					height: calc(100% + 4px);
					border-radius: 50%;
					border: $focus-width $focus-style var(--neutral-5);
				}
			}
		}

		&.is-range-day {
			background: rgba(var(--brand-2_rgb), 0.1);
			border-radius: 0;

			&:hover {
				background: var(--bg-brand-2);
				color: var(--bg-brand-2-fg-2);
				border-radius: $border-radius;
			}

			&.is-today {
				border: none;

				&::after {
					content: '';
					@include transform-full-center;
					width: 100%;
					height: 100%;
					border-radius: 50%;
					border: $focus-width $focus-style var(--neutral-5);
				}
			}
		}
	}

	&__weeks-of-month-day.not-same-month {
		.date-picker-calendar__weeks-of-month-day-text {
			opacity: 0.3;
		}
	}

	&__weeks-of-month-day-text {
		width: 100%;
		height: 100%;
	}
}
</style>
